/**
 * Lodestar — Sentry instrumentation.
 *
 * Imported FIRST in `src/index.js` so Sentry hooks the global error
 * handlers before any application code runs (the playbook is firm on
 * this — late init misses early errors).
 *
 * Configuration choices (user-approved iter 55):
 *   • Performance monitoring ON, tracesSampleRate = 0.01 (1%)
 *   • Session Replay OFF (privacy review needed before enabling)
 *   • Environment auto-tagging: REACT_APP_SENTRY_ENVIRONMENT from .env
 *     ("preview" locally; CI sets it to "production" on the prod build)
 *   • Release tagging: REACT_APP_SENTRY_RELEASE comes from CI, OR is
 *     read at runtime from /sentry-release.txt (written by the build-
 *     time source-map upload script). Without a release, source-maps
 *     won't resolve.
 *   • tracePropagationTargets: own backend URL + relative /api paths
 *     so frontend→backend traces correlate as a single transaction.
 */
import * as Sentry from "@sentry/react";

const dsn = process.env.REACT_APP_SENTRY_DSN;
const env = process.env.REACT_APP_SENTRY_ENVIRONMENT || "development";
const buildTimeRelease = process.env.REACT_APP_SENTRY_RELEASE;
const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

// Best-effort: if CI didn't inject the release at build time, fetch the
// release marker written by scripts/sentry-sourcemaps.js. Fire-and-
// forget so we never block first paint on this.
let runtimeRelease = buildTimeRelease;
async function tryFetchReleaseMarker() {
    if (runtimeRelease) return;
    try {
        const res = await fetch("/sentry-release.txt", { cache: "no-store" });
        if (res.ok) {
            const text = (await res.text()).trim();
            if (text) {
                runtimeRelease = text;
                // Sentry SDK exposes a runtime release setter via scope.
                Sentry.getCurrentScope?.().setTag("release", text);
            }
        }
    } catch {
        // No marker shipped (local dev `yarn start` without a build) — fine.
    }
}

if (dsn) {
    Sentry.init({
        dsn,
        environment: env,
        release: buildTimeRelease,
        integrations: [
            Sentry.browserTracingIntegration({
                tracePropagationTargets: [
                    backendUrl,
                    "https://lodestarastrology.com",
                    /^\/api/,
                ].filter(Boolean),
            }),
        ],
        // 1% performance trace sampling — playbook default for prod.
        tracesSampleRate: 0.01,
        // Default 1.0 error sampling — we want every crash.
        sampleRate: 1.0,
        // Defensive: ignore noise that's not actionable.
        ignoreErrors: [
            // Browser extensions firing into our window
            /^ResizeObserver loop/,
            /Non-Error promise rejection captured/,
        ],
    });
    tryFetchReleaseMarker();
}

export default Sentry;

